import { Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { ApiConnector, Table, RequestQueryPayload, ApiClientConstant } from '@cureskin/api-client';
import { ValueOf } from '@cureskin/api-client/src/common';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime, filter, mergeMap, Observable } from 'rxjs';
import { IframeService } from '../../../../services/iframe-service';
import { ConnectionService } from '../../../../services/connection-service';
import { AppConfig } from '../../../app.config';
import { Broadcaster } from '../../../../components/broadcaster';
import { InvestigationModule } from '../../../compare-progress/investigation';
import { LocalStorage } from '../../../../services/local-storage-service';
import { AudioRecorderComponent } from '../../../../components/audio-recorder/audio-recorder.component';
import { VoiceRecordingComponent } from '../../../../components/voice-recording/voice-recording.component';
import { ChatType } from '../../../../../typings/client/chat';
import { UploadFileService } from '../../../../services/uploadFileService/uploadFile.service';
import { HelperService } from '../../../../services/helper-service';

interface MessageData {
  mode?: string;
  message?: string;
  metadata?: string;
  buttonText?: string;
  dietLink?: string;
  regimenObj?: Table.Regimen;
  orderId?: string;
  notes?: string;
}
@Component({
  selector: 'app-ticket-message',
  templateUrl: './ticket-message.html',
  styleUrls: ['./ticket-message.scss'],
  standalone: false,
})
export class TicketMessageComponent {
  @Input() patient: Table.User;
  @Input() ticket: Table.SupportTicket;
  supportTicketId: string;
  pendingOrders: Array<Table.Order> = [];
  activeMainRegimens: Array<Table.Regimen> = [];
  data: MessageData = {};
  languageStringIdMap: { [key: string]: string };
  baseUrlForWebApp: string;
  internalUserRoles: Array<string> = [];
  blockInputAfterMessage: boolean;
  internalUser: Table.User;
  modes: Array<{ [key: string]: string }>;
  selectedOrder: Table.Order;
  moment: moment.Moment;
  mostRecentMessage: Table.SupportChat;
  ui: ChatType.UIType;
  file: File;
  placeHolder: string = 'Type message...';
  sendMode: boolean = false;
  selectedTeam: ValueOf<typeof ApiClientConstant.Role.Name>;
  teams: Array<string>;
  excludeTeams: Array<string> = [
    ApiClientConstant.Role.Name.FOLLOWUP,
    ApiClientConstant.Role.Name.REVIEW,
    ApiClientConstant.Role.Name.PAYMENT,
    ApiClientConstant.Role.Name.INVENTORY,
    ApiClientConstant.Role.Name.BetterHair,
    ApiClientConstant.Role.Name.WAITING_FOR_IMAGE,
    ApiClientConstant.Role.Name.Intern,
    ApiClientConstant.Role.Name.CHECK_IN,
  ];

  @ViewChild(AudioRecorderComponent) audioRecorderComponent: AudioRecorderComponent;
  @ViewChild(VoiceRecordingComponent) voiceRecordingComponent: VoiceRecordingComponent;
  constructor(public connectionService: ConnectionService,
    public broadcaster: Broadcaster,
    private dialog: MatDialog,
    private storage: LocalStorage,
    public appConfig: AppConfig,
    public iframeService: IframeService,
    private uploadFileService: UploadFileService,
    private helperService: HelperService) {
    this.moment = moment();
  }

  async ngOnInit(): Promise<void> {
    this.internalUser = this.connectionService.getCurrentUser();
    this.teams = Object.values(ApiClientConstant.Role.Name).filter((team: string) => !this.excludeTeams.includes(team));
    this.languageStringIdMap = ApiClientConstant.LanguageStringId;
    this.baseUrlForWebApp = this.connectionService.getWebAppUrl();
    this.internalUserRoles = this.storage.getJsonValue('userRoles');
    this.sendMode = !this.ticket.get('attended');
    this.initModes();
    this.reset();
    try {
      await this.findMostRecentMessage();
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: error.message, type: ApiClientConstant.Toast.Type.ERROR });
    }
  }


  async findMostRecentMessage(): Promise<void> {
    if (this.ticket.id) {
      if (!this.patient.id) {
        return;
      }
      const supportTicket = new Table.SupportTicket();
      supportTicket.id = this.ticket.id;
      [this.mostRecentMessage] = await this.connectionService.findSupportChat({
        where: { user: this.patient, supportTicket },
        descending: 'createdAt',
        limit: 1,
        project: ['context'],
      });
    }
  }

  reset(): void {
    this.ui = {
      page: 0,
      lazyload: true,
      loading: false,
      limit: 100,
      savingMessage: false,
    };
  }

  async onMessageModeChange(): Promise<void> {
    switch (this.data.mode) {
      case ApiClientConstant.Assistant.Mode.GENERIC_USER_INPUT:
      case ApiClientConstant.Assistant.Mode.PAYMENT: {
        await this.fetchUserPendingOrders();
        break;
      }
      case ApiClientConstant.Assistant.Mode.REGIMEN: {
        await this.getRegimens();
        break;
      }
      case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_SIDE_FACE: {
        await this.onImageRequest(this.languageStringIdMap.REQUEST_YOUR_SIDE_FACE_IMAGE, this.languageStringIdMap.TAKE_PHOTO);
        break;
      }
      case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_FRONT_FACE: {
        await this.onImageRequest(this.languageStringIdMap.REQUEST_YOUR_FRONT_FACE_IMAGE, this.languageStringIdMap.TAKE_PHOTO);
        break;
      }
      case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_HAIR: {
        await this.onImageRequest(this.languageStringIdMap.REQUEST_YOUR_HAIR_IMAGE, this.languageStringIdMap.TAKE_PHOTO);
        break;
      }
      case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_BODY: {
        await this.onImageRequest(this.languageStringIdMap.REQUEST_YOUR_BODY_IMAGE, this.languageStringIdMap.TAKE_PHOTO);
        break;
      }
      case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_REPORT: {
        await this.onImageRequest(this.languageStringIdMap.REQUEST_IMAGE_REPORT, this.languageStringIdMap.UPLOAD);
        break;
      }
      case ApiClientConstant.Assistant.Mode.REQUEST_REPORT: {
        const dialogRef = this.dialog.open(InvestigationModule);
        dialogRef.afterClosed().subscribe((test: { tests: { test: string, required: boolean }[], name?: string, index?: number }) => {
          if (test) {
            this.appendTestDetailsToMessage(test);
          }
        });
        await this.onImageRequest(this.languageStringIdMap.REQUEST_REPORT, this.languageStringIdMap.UPLOAD);
        break;
      }
      case ApiClientConstant.Assistant.Mode.AUDIO: {
        this.placeHolder = 'Type message after recording';
        break;
      }
      default:
        this.placeHolder = 'Type message...';
    }
  }

  async onImageRequest(messageStringId: string, buttonStringId: string): Promise<void> {
    const [languageString, takePhotoLanguageString]: Array<Table.LanguageString> = await Promise.all([
      this.connectionService.getLanguageString(messageStringId),
      this.connectionService.getLanguageString(buttonStringId),
    ]);
    const [message, buttonText]: Array<string> = await Promise.all([
      this.connectionService.getTranslatedStringOfUserLanguage(this.patient, languageString),
      this.connectionService.getTranslatedStringOfUserLanguage(this.patient, takePhotoLanguageString),
    ]);
    this.data.message = message;
    this.data.buttonText = buttonText;
  }

  async getRegimens(): Promise<void> {
    if (!this.activeMainRegimens?.length) {
      const payLoad: RequestQueryPayload<Table.Regimen> = {
        where: { active: true, type: ApiClientConstant.Regimen.Type.MAIN },
        project: ['title', 'regimenId'],
        ascending: 'regimenId',
        limit: 2000,
      };
      this.activeMainRegimens = await this.connectionService.findRegimens(payLoad);
    }
  }

  async fetchUserPendingOrders(): Promise<void> {
    if (!this.pendingOrders.length) {
      const orders = await this.connectionService.findOrders({
        where: {
          user: this.patient,
          stage: {
            $nin: [
              ApiClientConstant.Order.Stage.CANCELED,
              ApiClientConstant.Order.Stage.DELIVERED,
              ApiClientConstant.Order.Stage.INITIAL,
              ApiClientConstant.Order.Stage.RETURNED,
            ],
          },
        },
        project: ['orderNumber', 'type', 'amount'],
      });
      this.pendingOrders.push(...orders);
    }
  }

  async deleteLastOperatorMessage(): Promise<void> {
    const supportTicket = new Table.SupportTicket();
    supportTicket.id = this.ticket.id;
    const [message]: Array<Table.SupportChat> = await this.connectionService.findSupportChat({
      where: { user: this.patient, supportTicket },
      descending: 'createdAt',
      limit: 1,
    });
    try {
      await message.destroy();
      this.broadcaster.broadcast('NOTIFY', { message: 'Deleted last operator message', type: ApiClientConstant.Toast.Type.SUCCESS });
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: error.message, type: ApiClientConstant.Toast.Type.ERROR });
    }
    this.reloadChat();
  }

  changeSelectedOrder(index: number): void {
    const localIndex = +index;
    if (localIndex === -1) {
      this.selectedOrder = null;
      return;
    }
    this.selectedOrder = this.pendingOrders[localIndex];
  }

  afterSaveAudioFile(url: string): void {
    this.data.metadata = `audioPath=${url}`;
  }

  onAudioChange(audioChunks: Blob[] = []): void {
    if (!audioChunks.length) {
      this.data.message = '';
    }
  }

  initModes(): void {
    this.modes = [
      { key: ApiClientConstant.Assistant.Mode.WITH_TEXT_REPLY, value: 'Text input' },
      { key: ApiClientConstant.Assistant.Mode.AUDIO, value: 'Voice' },
      { key: ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_FRONT_FACE, value: 'Request front face image' },
      { key: ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_SIDE_FACE, value: 'Request side face image' },
      { key: ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_HAIR, value: 'Request hair image' },
      { key: ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_BODY, value: 'Request body image' },
      { key: ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_REPORT, value: 'Request report image' },
      { key: ApiClientConstant.Assistant.Mode.REQUEST_REPORT, value: 'Request report (Investigations / Tests)' },
      { key: ApiClientConstant.Assistant.Mode.GENERIC_USER_INPUT, value: 'Order status' },
      { key: ApiClientConstant.Assistant.Mode.CUSTOM_FORM, value: 'Consent Form' },
      { key: ApiClientConstant.Assistant.Mode.REGIMEN, value: 'Regimen' },
      { key: ApiClientConstant.Assistant.Mode.PAYMENT, value: 'Payment' },
      { key: ApiClientConstant.Assistant.Mode.CUSTOM_DIET, value: 'Send Custom Diet link' },
      { key: 'UPLOAD_IMAGE', value: 'Upload Image/PDF' },
    ];
  }

  async insertMessage(): Promise<void> {
    if (this.ui.savingMessage) return Promise.resolve();
    try {
      this.ui.savingMessage = true;
      this.prepareDoctorMessage();
      switch (this.data.mode) {
        case ApiClientConstant.Assistant.Mode.AUDIO:
          return this.handleAudioMode();
        case ApiClientConstant.Assistant.Mode.GENERIC_USER_INPUT:
          return this.handleGenericUserInputMode();
        case ApiClientConstant.Assistant.Mode.CUSTOM_DIET:
          return this.handleCustomDietMode();
        case ApiClientConstant.Assistant.Mode.REGIMEN:
          return this.addOrResendRegimen(this.data.regimenObj);
        case ApiClientConstant.Assistant.Mode.CUSTOM_FORM:
          return this.handleCustomFormMode();
        case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_FRONT_FACE:
        case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_SIDE_FACE:
        case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_HAIR:
        case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_BODY:
        case ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_REPORT:
        case ApiClientConstant.Assistant.Mode.REQUEST_REPORT:
          return this.handleRequestImageMode();
        case ApiClientConstant.Assistant.Mode.PAYMENT:
          return this.handlePaymentMode();
        case 'UPLOAD_IMAGE':
          return this.handleUploadImageMode();
        default:
          return this.sendMessageToUserAndUpdateChatWindow({
            msg: this.data.message,
            mode: this.data.mode,
            metadata: this.data.metadata,
            context: this.mostRecentMessage?.get('context'),
          });
      }
    } catch (err) {
      this.broadcaster.broadcast('NOTIFY', { message: err.message.toString(), type: ApiClientConstant.Toast.Type.ERROR });
      this.ui.savingMessage = false;
    }
    return Promise.resolve();
  }

  private prepareDoctorMessage(): void {
    if (this.internalUser.get('type') === 'doctor' && this.data.mode === 'WITH_TEXT_REPLY') {
      const imgPath = this.internalUser.get('DoctorImageThumbnail');
      const content = `${this.internalUser.get('DoctorDisplayName')} has sent you a message`;
      const url = `${this.baseUrlForWebApp}/userConfirmation?title=${content}&message=${this.data
        .message}&drImage=${imgPath}&time=${moment().format('LT')}`;
      this.data.metadata = `contentUrl:${url};thumbnail:${imgPath};content:${content};`;
    }
  }

  private async handleAudioMode(): Promise<void> {
    if (!this.data.metadata) {
      await this.voiceRecordingComponent.saveRecording();
      if (!this.data.metadata) return this.showErrorPopUp('Audio url is not present/saved.');
    }
    if (!this.data.message) {
      this.data.message = 'No AudioNote Available';
    }
    return this.sendMessageToUserAndUpdateChatWindow({
      msg: this.data.message,
      mode: this.data.mode,
      metadata: this.data.metadata,
      context: this.mostRecentMessage?.get('context'),
    });
  }

  private handleGenericUserInputMode(): Promise<void> {
    if (!this.selectedOrder) {
      this.showErrorPopUp('choose an order');
      return Promise.resolve();
    }
    return this.connectionService.sendUserOrderStatusMessage(this.patient.get('username'), this.selectedOrder.id);
  }

  private handleCustomDietMode(): Promise<void> {
    if (!this.data.dietLink) {
      this.showErrorPopUp('Enter Diet Link');
      return Promise.resolve();
    }
    this.blockInputAfterMessage = false;
    const Inputs = [{
      type: ApiClientConstant.Question.InputType.BUTTON,
      action: ApiClientConstant.Question.InputAction.ACTION,
      params: { actionName: 'OpenInCustomTab', url: this.data.dietLink },
      text: 'View Diet',
    }];
    return this.sendMessageToUserAndUpdateChatWindow({
      msg: this.data.message,
      mode: ApiClientConstant.Assistant.Mode.STICKY_ACTION,
      Inputs,
      params: {},
      context: this.mostRecentMessage?.get('context'),
    });
  }

  private async handleCustomFormMode(): Promise<void> {
    let questionId: string;
    switch (this.patient.get('Gender')) {
      case 'Male':
        questionId = this.patient.get('languagePreference') === 'hi'
          ? 'consentFormIsotretinoinMaleHi'
          : 'consentFormIsotretinoinMaleEn';
        break;
      case 'Female':
        questionId = this.patient.get('languagePreference') === 'hi'
          ? 'consentFormIsotretinoinFemaleHi'
          : 'consentFormIsotretinoinFemaleEn';
        break;
      default:
        this.broadcaster.broadcast('NOTIFY', { message: 'Gender is not present', type: ApiClientConstant.Toast.Type.ERROR });
        return Promise.resolve();
    }
    const context = { supportTicketId: this.ticket.id };
    await this.connectionService.sendQuestionTo(questionId, this.patient.get('username'), context);
    await this.connectionService.sendQuestionTo('consentFormIsotretinoinAcknowledgement', this.patient.get('username'), context);
    this.showMessageSentToastAndResetChatInput();
    return Promise.resolve();
  }

  private handleRequestImageMode(): Promise<void> {
    const tagMap = {
      [ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_FRONT_FACE]: ApiClientConstant.InstantCheckup.Type.FRONT_FACE,
      [ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_SIDE_FACE]: ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
      [ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_HAIR]: ApiClientConstant.InstantCheckup.Type.HAIR_FRONT,
      [ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_BODY]: ApiClientConstant.InstantCheckup.Type.BODY,
      [ApiClientConstant.Assistant.Mode.REQUEST_IMAGE_REPORT]: ApiClientConstant.InstantCheckup.Type.REPORT,
      [ApiClientConstant.Assistant.Mode.REQUEST_REPORT]: 'UploadReport',
    };
    const actionName = this.data.mode === ApiClientConstant.Assistant.Mode.REQUEST_REPORT ? 'UploadReport' : 'InstantCheckup';
    const queryParams = this.data.mode === ApiClientConstant.Assistant.Mode.REQUEST_REPORT ? {} : { tag: tagMap[this.data.mode] };
    const Inputs = [{
      type: ApiClientConstant.Question.InputType.BUTTON,
      action: ApiClientConstant.Question.InputAction.ACTION,
      params: { actionName, queryParams: JSON.stringify(queryParams) },
      text: this.data.buttonText,
    }];
    return this.sendMessageToUserAndUpdateChatWindow({
      msg: this.data.message,
      mode: ApiClientConstant.Assistant.Mode.STICKY_ACTION,
      Inputs,
      params: {},
      context: this.mostRecentMessage?.get('context'),
    });
  }

  private handlePaymentMode(): Promise<void> {
    const order = this.pendingOrders.find((o: Table.Order) => (o.id === this.data.orderId));
    const Inputs = [{
      type: ApiClientConstant.Question.InputType.BUTTON,
      action: ApiClientConstant.Question.InputAction.ACTION,
      params: { actionName: 'Checkout', url: `/user/order/${this.data.orderId}/payment` },
      text: `Pay (Rs. ${order.get('amount')})`,
    }];
    return this.sendMessageToUserAndUpdateChatWindow({
      msg: this.data.message,
      mode: ApiClientConstant.Assistant.Mode.STICKY_ACTION,
      params: { notes: this.data.notes, orderId: this.data.orderId },
      Inputs,
      context: this.mostRecentMessage?.get('context'),
    });
  }

  async sendMessageToUserAndUpdateChatWindow({ msg, mode, metadata, metadataAction, params, Inputs, context }: ChatType.MessageType)
    : Promise<void> {
    this.saveMessage({ msg, mode, metadata, metadataAction, params, Inputs, context });
  }

  async saveMessage({ msg, mode, metadata, metadataAction, params, Inputs, imageCompareLinkFlag }: ChatType.MessageType): Promise<void> {
    if (this.ticket) {
      const ticket = new Table.SupportTicket();
      ticket.id = this.ticket.id;
      try {
        await this.connectionService.replyToUserTicket({
          ticket,
          user: this.patient,
          userId: this.patient.get('username'),
          msg,
          mode,
          metadata,
          Inputs,
          context: { supportTicketId: this.ticket.id },
        });
        this.data = {};
        this.reset();
        await this.findMostRecentMessage();
        await this.showMessageSentToastAndResetChatInput();
      } catch (error) {
        this.ui.savingMessage = false;
        this.showErrorPopUp(error.message);
      }
      return;
    }
    this.broadcaster.broadcast('NOTIFY', { message: 'message cannot be sent from here',
      type: ApiClientConstant.Toast.Type.ERROR });
  }

  async showMessageSentToastAndResetChatInput(): Promise<void> {
    this.showSuccessPopUp('Message sent');
    setTimeout(async () => {
      await this.fetchTicket();
      this.sendMode = !this.ticket.get('attended');
    }, 1000);
    this.reloadChat();
  }

  async addOrResendRegimen(currentObj: Table.Regimen): Promise<void> {
    try {
      this.ui.savingMessage = true;
      const regimenId: string = currentObj.get('regimenId');
      await this.connectionService.sendOrCreatePersonalizedRegimen(
        regimenId,
        this.patient.get('username'),
        { supportTicketId: this.ticket.id });
      this.ui.savingMessage = false;
      this.showMessageSentToastAndResetChatInput();
    } catch (err) {
      this.ui.savingMessage = false;
      this.showErrorPopUp(err.message);
    }
  }

  showErrorPopUp(message: string): void {
    this.broadcaster.broadcast('NOTIFY', { message, type: ApiClientConstant.Toast.Type.ERROR });
  }

  showSuccessPopUp(message: string): void {
    this.broadcaster.broadcast('NOTIFY', { message, type: ApiClientConstant.Toast.Type.SUCCESS });
  }

  toggleSendMode(): void {
    this.sendMode = !this.sendMode;
  }

  selectTeam(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedTeam = target.value as ValueOf<typeof ApiClientConstant.Role.Name>;
  }

  async assignTicket(): Promise<void> {
    this.ticket.set('teams', [this.selectedTeam]);
    try {
      await this.ticket.save();
      this.showSuccessPopUp('Ticket assigned successfully');
    } catch (error) {
      this.showErrorPopUp(error.message);
    }
  }

  async requestClosure(): Promise<void> {
    this.ticket.set('status', ApiClientConstant.SupportTicket.Status.RequestClosure);
    try {
      this.ticket = await this.ticket.save();
      this.showSuccessPopUp('Ticket Closure request sent succesfully');
    } catch (error) {
      this.showErrorPopUp(error);
    }
  }

  async fetchTicket(): Promise<void> {
    this.ticket = await this.connectionService.fetchSupportTicket({
      where: { objectId: this.ticket.id },
      include: ['titleLanguageString'],
    });
  }

  private appendTestDetailsToMessage(test: { tests: { test: string, required: boolean }[], name?: string }): void {
    if (this.data.message) {
      this.data.message += '<br> <br>';
    }
    if (test.name) {
      this.data.message += `${test.name}<br> <br>`;
    }
    test.tests.forEach((value: { test: string, required: boolean }, index: number) => {
      this.data.message += `${index + 1}. ${value.test}<br>`;
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.file = input.files[0];
    }
  }

  private async handleUploadImageMode(): Promise<void> {
    if (!this.file) {
      this.showErrorPopUp('Please select a file');
      this.ui.savingMessage = false;
      return;
    }
    try {
      const url = await this.uploadFileService.uploadFile({
        file: this.file,
        bucket: 'OTHER',
        username: this.patient.get('username'),
        source: 'doctor_other',
      });
      const userFile = new Table.UserFiles();
      userFile.set('fileUrl', url);
      userFile.set('createdBy', ApiConnector.getCurrentUser().get('type').toUpperCase());
      userFile.set('user', this.patient);
      userFile.set('type', this.helperService.findFileType(this.file.name));
      await userFile.save({}, { context: { displayInChat: true, supportTicketId: this.ticket.id } });
      this.file = null;
      this.data = {};
      this.showSuccessPopUp('File uploaded and sent');
      this.reloadChat();
    } catch (error) {
      this.showErrorPopUp(error.message);
    }
    this.ui.savingMessage = false;
  }

  reloadChat(): void {
    this.iframeService.updateIframeUrl(`${this.connectionService
      .getWebAppUrl()}/chatV2/${this.ticket.id}?type=supportTicket&loginType=token&username=${this.patient.get('username')}`);
  }

  protected apiClientConstant: typeof ApiClientConstant = ApiClientConstant;
}
